const ONE_HOUR = 60 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const ONE_MINUTE = 60 * 1000;

/**
 * Length of a slot or consultation written before per-slot durations existed.
 * Every duration-aware helper below falls back to this, so a caller that passes
 * nothing behaves exactly as it did when everything was an hour long.
 */
const DEFAULT_DURATION_MINUTES = 60;

/**
 * Get the day of the week from a Date object
 * @param {Date} date
 * @returns {String}
 */
function getDayOfTheWeek(date) {
  const day = date.getDay();
  switch (day) {
    case 0:
      return "sunday";
    case 1:
      return "monday";
    case 2:
      return "tuesday";
    case 3:
      return "wednesday";
    case 4:
      return "thursday";
    case 5:
      return "friday";
    case 6:
      return "saturday";
    default:
      return "Unknown";
  }
}

/**
 *Return if date is today
 * @param {Date} date
 * @returns {Boolean}
 */
function isDateToday(date) {
  return new Date().toDateString() === date.toDateString();
}

/**
 *Return if date is yesterday
 * @param {Date} date
 * @returns {Boolean}
 */
const isDateYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return yesterday.toDateString() === date.toDateString();
};

/**
 * Get time from Date object
 * @param {Date} date
 * @returns {String} in hh:mm format
 */
const getTimeFromDate = (date) => {
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Get the starting and ending day of the week
 *
 * @param {Date} day
 * @returns {{ firstDayOfWeek, lastDayOfWeek }} Date objects for the first and last days of the week
 */
function getStartAndEndOfWeek(day) {
  const weekMap = [6, 0, 1, 2, 3, 4, 5];
  const now = new Date(day);
  now.setHours(0, 0, 0, 0);
  const firstDayOfWeek = new Date(now);
  firstDayOfWeek.setDate(
    firstDayOfWeek.getDate() - weekMap[firstDayOfWeek.getDay()]
  );
  const lastDayOfWeek = new Date(now);
  lastDayOfWeek.setDate(
    lastDayOfWeek.getDate() - weekMap[lastDayOfWeek.getDay()] + 6
  );
  lastDayOfWeek.setHours(23, 59, 59, 999);
  return { first: firstDayOfWeek, last: lastDayOfWeek };
}

function getDatesInRange(start, end) {
  const dates = [];
  const currDate = new Date(start);
  const lastDate = new Date(end);
  while (currDate <= lastDate) {
    dates.push(new Date(currDate));
    currDate.setDate(currDate.getDate() + 1);
  }
  return dates;
}

/**
 * Return if date is between two dates
 * @param {Date} date
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Boolean}
 */
function isDateBetweenTwoDates(date, startDate, endDate) {
  return date >= startDate && date <= endDate;
}

/**
 * Return the time as String
 * @param {Date} date
 * @returns {String} in hh:mm format
 */
function getTimeAsString(date) {
  return date
    ? `${date.getHours() > 9 ? date.getHours() : "0" + date.getHours()}:${
        date.getMinutes() > 9 ? date.getMinutes() : "0" + date.getMinutes()
      }`
    : "";
}

/**
 * Get the month name as String from a Date object
 * @param {Date} date
 * @returns {String}
 */
function getMonthName(date) {
  const month = date.getMonth();
  switch (month) {
    case 0:
      return "January";
    case 1:
      return "February";
    case 2:
      return "March";
    case 3:
      return "April";
    case 4:
      return "May";
    case 5:
      return "June";
    case 6:
      return "July";
    case 7:
      return "August";
    case 8:
      return "September";
    case 9:
      return "October";
    case 10:
      return "November";
    case 11:
      return "December";
    default:
      return "Unknown";
  }
}

function getDateView(date) {
  const newDate = new Date(date);
  const day = newDate.getDate();
  const month = newDate.getMonth() + 1;
  const fullYear = newDate.getFullYear();
  const year = fullYear.toString().slice(-2);

  return `${day < 10 ? `0${day}` : day}.${
    month < 10 ? `0${month}` : month
  }.${year}`;
}

/**
 *
 * @param {Date} day date object
 * @param {string} hour - in format "HH:MM"
 */
function getDateAsFullString(day, hour) {
  return new Date(`${getDateDashes(day)}T${hour}`).toString();
}

/**
 * Format date from the js Date object Thu Mar 25 2021 00:00:00 GMT+0000 (Greenwich Mean Time) to YYYY-MM-DD
 *
 * @param {Date} date the formatted js Date object
 * @returns {string} the formatted DB date YYYY-MM-DD
 */
function getDateDashes(date) {
  let d = new Date(date),
    month = "" + (d.getMonth() + 1),
    day = "" + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
}

/**
 * Get the timestamp as UTC
 *
 * @param {Date} date Date object
 * @param {string} time format HH:MM
 * @returns
 */
function getTimestamp(date, time = "00:00") {
  return new Date(`${getDateDashes(date)}T${time}`).getTime() / 1000;
}

/**
 * Get the timestamp from UTC date
 *
 * @param {Date} date Date object
 * @param {string} time format HH:MM
 * @returns
 */
function getTimestampFromUTC(date, time = "00:00") {
  return new Date(`${getDateDashes(date)}T${time}Z`).getTime() / 1000;
}

function getXDaysInSeconds(x) {
  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;

  return x * day;
}

const hours = [
  "00:00",
  "01:00",
  "02:00",
  "03:00",
  "04:00",
  "05:00",
  "06:00",
  "07:00",
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
  "21:00",
  "22:00",
  "23:00",
];

/**
 * The list of times in a day on a given step, as "HH:MM".
 *
 * `slotTimes(60)` reproduces the `hours` array above; `slotTimes(30)` is the
 * 48-entry half-hour grid the provider calendar uses.
 *
 * @param {number} stepMinutes must divide 60
 * @returns {string[]}
 */
function slotTimes(stepMinutes = 30) {
  const times = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += stepMinutes) {
    const h = String(Math.floor(minutes / 60)).padStart(2, "0");
    const m = String(minutes % 60).padStart(2, "0");
    times.push(`${h}:${m}`);
  }
  return times;
}

/**
 * When does a consultation (or slot) that starts at `startMs` end?
 *
 * @param {number|Date|string} startMs start, in ms since epoch or anything Date-like
 * @param {number} [durationMinutes] defaults to the legacy hour
 * @returns {Date}
 */
function getConsultationEndDate(startMs, durationMinutes) {
  const start =
    startMs instanceof Date ? startMs.getTime() : new Date(startMs).getTime();
  const minutes =
    Number(durationMinutes) > 0
      ? Number(durationMinutes)
      : DEFAULT_DURATION_MINUTES;
  return new Date(start + minutes * ONE_MINUTE);
}

/**
 * Render a consultation's time range, e.g. "16:00 - 16:30".
 *
 * @param {number|Date|string} startMs
 * @param {number} [durationMinutes]
 * @returns {string}
 */
function getTimeRangeAsString(startMs, durationMinutes) {
  const start = startMs instanceof Date ? startMs : new Date(startMs);
  return `${getTimeAsString(start)} - ${getTimeAsString(
    getConsultationEndDate(start, durationMinutes),
  )}`;
}

/**
 * Is a consultation joinable right now? True from five minutes before it starts
 * until it ends.
 *
 * @param {number} timestamp start, in milliseconds
 * @param {number} [durationMinutes] defaults to the legacy hour
 *
 * @returns {boolean}
 */
function checkIsFiveMinutesBefore(timestamp, durationMinutes) {
  const currentTime = new Date().getTime();
  const endTime = getConsultationEndDate(timestamp, durationMinutes).getTime();

  return currentTime >= timestamp - FIVE_MINUTES && currentTime <= endTime;
}

function getOrdinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `ordinal_${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/**
 * Format time from the js Date object Thu Mar 25 2021 00:00:00 GMT+0000 (Greenwich Mean Time) to HH:MM
 *
 * @param {Date} date the formatted js Date object
 * @returns {string} the formatted time HH:MM
 */
function getTime(date) {
  let d = new Date(date),
    timeText = d.toTimeString();

  return timeText.split(" ")[0].slice(0, -3);
}

const parseUTCDate = (dateString) => {
  const dateParams = dateString.replace(/ UTC/, "").split(/[\s-:]/);
  dateParams[1] = (parseInt(dateParams[1], 10) - 1).toString();

  return new Date(Date.UTC(...dateParams));
};

const getFirstAndLastDayOfPastMonth = () => {
  const now = new Date();
  // Get the first day of the current month, then subtract one day to get the last day of the past month
  let lastDayOfPastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

  lastDayOfPastMonth.setHours(23, 59, 59, 999);

  // Get the first day of the past month by setting the day to 1 for the last day of the past month
  let firstDayOfPastMonth = new Date(
    lastDayOfPastMonth.getFullYear(),
    lastDayOfPastMonth.getMonth(),
    1
  );

  firstDayOfPastMonth.setHours(0, 0, 0, 0);

  return {
    firstDay: getDateDashes(firstDayOfPastMonth),
    lastDay: getDateDashes(lastDayOfPastMonth),
  };
};

/**
 * "HH:MM - HH:MM DD.MM.YY" for a consultation.
 *
 * @param {Date} date start
 * @param {number} [durationMinutes] defaults to the legacy hour
 */
const formatDateWithTimeRange = (date, durationMinutes) => {
  const padZero = (num) => (num < 10 ? `0${num}` : num);

  const hours = padZero(date.getHours());
  const minutes = padZero(date.getMinutes());

  // The real end, so a 30-minute consultation reads 16:00 - 16:30 and not 16:00 - 17:00.
  const endDate = getConsultationEndDate(date, durationMinutes);
  const endHours = padZero(endDate.getHours());
  const endMinutes = padZero(endDate.getMinutes());

  // Format the date as DD.MM.YY
  const day = padZero(date.getDate());
  const month = padZero(date.getMonth() + 1); // Months are 0-indexed
  const year = String(date.getFullYear()).slice(-2); // Get last two digits of the year

  return `${hours}:${minutes} - ${endHours}:${endMinutes} ${day}.${month}.${year}`;
};

export {
  getDayOfTheWeek,
  isDateToday,
  isDateYesterday,
  getTimeFromDate,
  getStartAndEndOfWeek,
  getDatesInRange,
  isDateBetweenTwoDates,
  getTimeAsString,
  getMonthName,
  getDateView,
  getDateDashes,
  getTimestamp,
  getTimestampFromUTC,
  getDateAsFullString,
  getXDaysInSeconds,
  checkIsFiveMinutesBefore,
  getOrdinal,
  hours,
  slotTimes,
  getConsultationEndDate,
  getTimeRangeAsString,
  ONE_HOUR,
  ONE_MINUTE,
  DEFAULT_DURATION_MINUTES,
  FIVE_MINUTES,
  getTime,
  parseUTCDate,
  getFirstAndLastDayOfPastMonth,
  formatDateWithTimeRange,
};
